import React, { useState } from 'react';
import styled from 'styled-components';

import { Blurb } from 'components/Containers';
import ProjectedDischargeModal from 'components/modals/ProjectedDischargeModal';
import AccessGuard from 'components/shared/AccessGuard';
import Tooltip from 'components/shared/Tooltip';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import Profile, { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { BodySmallBold, Label, LabelBold } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';
import PencilIcon from 'svg/PencilIcon';

type ProjectedDischargeType = {
  locationEpisode: LocationEpisode;
  episode: Episode;
  patientName: string;
  profile: Profile;
};

export function ProjectedDischarge(props: ProjectedDischargeType) {
  const [showModal, setShowModal] = useState(false);
  const { translate } = useTranslation();

  const { locationEpisode, episode, patientName, profile } = props;
  const projectedDischargeReview = locationEpisode.projectedDischargeReview;

  const currentGroupTypeClientConfig = locationEpisode?.owner?.client?.configForManagedGroupType(
    locationEpisode?.group?.groupTypeId
  );

  const showProjectedDischarge =
    (profile.has(PERMISSIONS.reviewProjectedDischargeCreate, { episode, locationEpisode }) &&
      currentGroupTypeClientConfig?.projectedDischarge.enabled &&
      !locationEpisode?.discharged &&
      !episode?.archived &&
      !locationEpisode?.archived) ||
    (projectedDischargeReview && !projectedDischargeReview?.pending);

  const showUpdate =
    (profile.isManagerFor(episode) || profile.belongsToOwningClientFor(episode)) &&
    !locationEpisode.archived &&
    locationEpisode.latest &&
    !episode.archived;

  const date = projectedDischargeReview?.date;

  return !showProjectedDischarge ? (
    <></>
  ) : (
    <Blurb data-cy='projectedDischargeDate'>
      <DischargeHeader>
        <Label>Projected {translate('Discharge')}</Label>
        <Tooltip text='This date is provided by the risk-bearing entity' position='right'>
          <StyledInfoIcon />
        </Tooltip>
        <Spacer />
        {showUpdate && (
          <AccessGuard
            accessor={
              projectedDischargeReview
                ? PERMISSIONS.reviewProjectedDischargeEdit
                : PERMISSIONS.reviewProjectedDischargeCreate
            }
            locationEpisode={locationEpisode}
            episode={episode}>
            <ActionContainer data-cy='clickable' onClick={() => setShowModal(true)}>
              <PencilIcon width={12} height={12} color={colors.primaryBlue} />
              <StyledLabel>Update</StyledLabel>
            </ActionContainer>
          </AccessGuard>
        )}
      </DischargeHeader>
      <DischargeDate $isOverdue={locationEpisode.dischargeOverdue}>
        <BodySmallBold>{date ? simpleDate(date) : '—'}</BodySmallBold>
      </DischargeDate>
      {showModal && (
        <ProjectedDischargeModal
          review={projectedDischargeReview}
          setShow={setShowModal}
          patientName={patientName}
          locationEpisode={locationEpisode}
        />
      )}
    </Blurb>
  );
}

const DischargeHeader = styled.div`
  display: flex;
  padding: 24px 24px 8px;
  align-items: center;
`;

const StyledInfoIcon = styled(InfoIconOutline)`
  width: 14px;
  height: 14px;
  font-size: 14px;
  margin-left: 8px;
  color: ${colors.black75};
`;

const DischargeDate = styled.div<{ $isOverdue: boolean }>`
  color: ${(props) => props.$isOverdue && 'var(--accent-red)'};
  padding: 0 24px 24px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const StyledLabel = styled(LabelBold)`
  margin-left: 8px;
  cursor: pointer;
  color: var(--primary-blue);
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
